import { RowActions } from '@/features/approvals/components/RowActions';
import { useEventConfig } from '@/shared/config/event-config-context';
import { cn } from '@/shared/lib/cn';
import { formatRegisteredAt } from '@/shared/lib/datetime';
import { initialsOf } from '@/shared/lib/initials';
import type { Participant } from '@/shared/types/pax';

interface ParticipantCardsProps {
  participants: Participant[];
  currentGroup: string;
  pendingIds: ReadonlySet<number>;
  onMove: (participant: Participant, target: string) => void;
}

function Field({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="shrink-0 text-[0.75rem] text-ink-subtle">{label}</dt>
      <dd className="truncate text-right text-[0.8125rem] text-ink-muted">{value ?? '—'}</dd>
    </div>
  );
}

/**
 * Visualização mobile: um cartão por participante.
 *
 * O painel antigo transformava a mesma `<table>` em cartões via CSS
 * (`table td { display: block }` + `data-label`), o que gera semântica de
 * tabela sem estrutura de tabela. Aqui são duas árvores distintas e apenas uma
 * é montada por vez — ver `useMediaQuery` em `ApprovalsPage`.
 */
export function ParticipantCards({ participants, currentGroup, pendingIds, onMove }: ParticipantCardsProps) {
  const { columns } = useEventConfig();

  return (
    <ul className="divide-y divide-line">
      {participants.map(participant => {
        const pending = pendingIds.has(participant.id);

        return (
          <li key={participant.id} className={cn('px-4 py-4 transition-opacity', pending && 'opacity-55')}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2.5">
                <span
                  aria-hidden="true"
                  className="grid size-9 shrink-0 place-items-center rounded-full bg-surface-subtle text-[0.6875rem] font-semibold text-ink-muted"
                >
                  {initialsOf(participant.nome)}
                </span>
                {/* O nome vem de `TB_PAX.nome`, não de atributo: um cartão sem
                    título não é identificável. O resto são as colunas da
                    parametrização, na lista abaixo. */}
                <span className="truncate font-medium text-ink">{participant.nome}</span>
              </div>

              <RowActions participant={participant} currentGroup={currentGroup} pending={pending} onMove={onMove} />
            </div>

            <dl className="mt-3 space-y-1 rounded-lg bg-surface-muted px-3 py-2.5">
              {columns.map(column => (
                <Field key={column.attributeId} label={column.label} value={participant.values[column.attributeId] ?? null} />
              ))}
              <Field label="Cadastro" value={formatRegisteredAt(participant.registeredAt)} />
            </dl>
          </li>
        );
      })}
    </ul>
  );
}
